import { getViewportForBounds, type Node } from "@xyflow/react";
import { toPng } from "html-to-image";
import { jsPDF } from "jspdf";
import {
  DEFAULT_MAP_TITLE,
  isMapKind,
  MAP_EXPORT_FORMAT,
  MAP_EXPORT_VERSION,
  mindmapDocSchema,
  type MapExportFile,
} from "@etus-mindmap/shared/api";

// Export/import do mapa. JSON = round-trip lossless (envelope MapExportFile).
// PNG/PDF = saída visual do canvas inteiro enquadrado (não reimportável).

const PAD = 0.15; // margem em volta do conteúdo (fração)
const MAX = 4096; // teto por lado, evita canvas gigante em mapas enormes

type Rect = { x: number; y: number; width: number; height: number };

// Bounds de TODOS os nós incluindo a largura/altura medida. O `getNodesBounds`
// standalone do @xyflow lê dimensões legadas e volta bounds só pelas posições
// (nós à direita/baixo estouravam e a imagem saía cortada) — computamos direto
// de `position` + `measured`.
function nodesBounds(nodes: Node[]): Rect {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const n of nodes) {
    const w = n.measured?.width ?? n.width ?? 0;
    const h = n.measured?.height ?? n.height ?? 0;
    const x = n.position?.x ?? 0;
    const y = n.position?.y ?? 0;
    minX = Math.min(minX, x);
    minY = Math.min(minY, y);
    maxX = Math.max(maxX, x + w);
    maxY = Math.max(maxY, y + h);
  }
  if (!Number.isFinite(minX)) return { x: 0, y: 0, width: 0, height: 0 };
  return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
}

// Bounds lidos do DOM real que será capturado: cada `.react-flow__node` carrega
// `translate(x, y)` em coordenadas do flow e offsetWidth/Height sem escala.
// `node.measured` do estado React pode estar ausente em alguns nós na hora do
// export (vimos nós à direita/baixo contarem com largura 0 → PNG cortado); o
// DOM é a verdade do que o html-to-image vai renderizar. Retorna null se nenhum
// nó der match (aí caímos no cálculo por estado acima).
function domNodesBounds(viewport: HTMLElement): Rect | null {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const el of Array.from(viewport.querySelectorAll<HTMLElement>(".react-flow__node"))) {
    const m = /translate\(\s*(-?[\d.]+)px[,\s]+(-?[\d.]+)px\s*\)/.exec(el.style.transform);
    if (!m) continue;
    const x = parseFloat(m[1]);
    const y = parseFloat(m[2]);
    minX = Math.min(minX, x);
    minY = Math.min(minY, y);
    maxX = Math.max(maxX, x + el.offsetWidth);
    maxY = Math.max(maxY, y + el.offsetHeight);
  }
  if (!Number.isFinite(minX)) return null;
  return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
}

function triggerDownload(href: string, filename: string) {
  const a = document.createElement("a");
  a.href = href;
  a.download = filename;
  a.click();
}

// Nome de arquivo a partir do título (sem acentos/símbolos).
export function slugify(title: string): string {
  const s = title
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return s || "mapa";
}

// As arestas do React Flow v12 são <path> cujo `stroke`/`fill`/`stroke-width`
// vêm da folha de estilo (.react-flow__edge-path), não de estilo inline. O
// html-to-image NÃO inlina propriedades de apresentação SVG vindas de CSS, então
// os paths saem sem traço (invisíveis). Copiamos os valores computados para
// estilo inline antes de capturar e restauramos depois. Sem isso, "as linhas
// não aparecem" no PNG/PDF.
function inlineEdgeStrokes(root: HTMLElement): () => void {
  const paths = Array.from(root.querySelectorAll<SVGPathElement>(".react-flow__edge-path"));
  const saved = paths.map((p) => ({ stroke: p.style.stroke, strokeWidth: p.style.strokeWidth, fill: p.style.fill }));
  for (const p of paths) {
    const cs = getComputedStyle(p);
    p.style.stroke = cs.stroke;
    p.style.strokeWidth = cs.strokeWidth;
    p.style.fill = cs.fill; // normalmente "none": path é linha, não preenchimento
  }
  return () => {
    paths.forEach((p, i) => {
      p.style.stroke = saved[i].stroke;
      p.style.strokeWidth = saved[i].strokeWidth;
      p.style.fill = saved[i].fill;
    });
  };
}

// Enquadra TODOS os nós numa imagem própria (independente do zoom atual),
// seguindo o padrão do React Flow (getNodesBounds + getViewportForBounds).
async function renderPng(
  root: HTMLElement,
  nodes: Node[],
  backgroundColor: string,
): Promise<{ dataUrl: string; w: number; h: number }> {
  const el = root.querySelector<HTMLElement>(".react-flow__viewport");
  if (!el) throw new Error("Canvas do mapa não encontrado.");
  const bounds = domNodesBounds(el) ?? nodesBounds(nodes);
  const w = Math.min(Math.max(Math.ceil(bounds.width * (1 + PAD * 2)), 400), MAX);
  const h = Math.min(Math.max(Math.ceil(bounds.height * (1 + PAD * 2)), 300), MAX);
  // minZoom baixo: mapas largos precisam zoom < 1 p/ caberem inteiros (senão o
  // React Flow clampa o zoom e a imagem sai cortada).
  const viewport = getViewportForBounds(bounds, w, h, 0.05, 2, PAD);
  const restoreEdges = inlineEdgeStrokes(el);
  try {
    return {
      dataUrl: await toPng(el, {
        backgroundColor,
        width: w,
        height: h,
        pixelRatio: 2,
        style: {
          width: `${w}px`,
          height: `${h}px`,
          transform: `translate(${viewport.x}px, ${viewport.y}px) scale(${viewport.zoom})`,
        },
      }),
      w,
      h,
    };
  } finally {
    restoreEdges();
  }
}

export async function downloadMapPng(
  root: HTMLElement,
  nodes: Node[],
  backgroundColor: string,
  title: string,
) {
  if (nodes.length === 0) throw new Error("Mapa vazio — nada para exportar.");
  const { dataUrl } = await renderPng(root, nodes, backgroundColor);
  triggerDownload(dataUrl, `${slugify(title)}.png`);
}

export async function downloadMapPdf(
  root: HTMLElement,
  nodes: Node[],
  backgroundColor: string,
  title: string,
) {
  if (nodes.length === 0) throw new Error("Mapa vazio — nada para exportar.");
  const { dataUrl, w, h } = await renderPng(root, nodes, backgroundColor);
  const pdf = new jsPDF({ orientation: w >= h ? "landscape" : "portrait", unit: "px", format: [w, h] });
  pdf.addImage(dataUrl, "PNG", 0, 0, w, h);
  pdf.save(`${slugify(title)}.pdf`);
}

export function downloadMapJson(file: MapExportFile) {
  const blob = new Blob([JSON.stringify(file, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  triggerDownload(url, `${slugify(file.title)}.etusmind.json`);
  URL.revokeObjectURL(url);
}

// Constrói o envelope a partir do estado atual do editor.
export function buildExportFile(kind: MapExportFile["kind"], title: string, doc: MapExportFile["doc"]): MapExportFile {
  return { format: MAP_EXPORT_FORMAT, version: MAP_EXPORT_VERSION, kind, title, doc };
}

// Valida e normaliza um arquivo importado. Espelha a validação do worker para
// dar erro claro antes de bater na API. Lança Error com mensagem amigável.
export function parseMapExport(text: string): MapExportFile {
  let raw: unknown;
  try {
    raw = JSON.parse(text);
  } catch {
    throw new Error("Arquivo não é um JSON válido.");
  }
  if (!raw || typeof raw !== "object") throw new Error("Formato de arquivo inválido.");
  const o = raw as Record<string, unknown>;
  if (o.format !== MAP_EXPORT_FORMAT) throw new Error("Não é um arquivo de mapa do etus-mindmap.");
  if (!isMapKind(o.kind)) throw new Error("Tipo de mapa desconhecido no arquivo.");
  const parsed = mindmapDocSchema.safeParse(o.doc);
  if (!parsed.success) throw new Error("Conteúdo do mapa inválido ou corrompido.");
  const title = typeof o.title === "string" && o.title.trim() ? o.title.trim() : DEFAULT_MAP_TITLE;
  const version = typeof o.version === "number" ? o.version : MAP_EXPORT_VERSION;
  return { format: MAP_EXPORT_FORMAT, version, kind: o.kind, title, doc: parsed.data };
}
